// Sax.net player
// Copyright 2020, Sax.net

import { Howl, Howler } from "howler";

export default class Player {
  private element: HTMLElement | null;
  private playButton: HTMLElement | null;
  private timeLeftLabel: HTMLElement | null;
  private soundUrl: string;
  private declaredDuration: number;
  private _howl!: Howl;
  private timerHandle: number | undefined;

  constructor(htmlElement: HTMLElement) {
    this.element = htmlElement;

    // Create and append controls (don't replace existing content like images)
    const duration = document.createElement("div");
    duration.className = "duration";
    const playButton = document.createElement("div");
    playButton.className = "play-button";
    this.element.appendChild(duration);
    this.element.appendChild(playButton);

    this.playButton = playButton;
    this.timeLeftLabel = duration;

    let soundPath = this.element.getAttribute("data-sound-url");
    this.soundUrl = `https://s3.amazonaws.com/files.sax.net/sound/${soundPath}`;
    this.declaredDuration = Number(this.element.getAttribute("data-duration"));

    this.element.addEventListener("click", (event) => {
      this.play();
      event.preventDefault();
      event.stopPropagation();
      return false;
    });

    this.updateTimeLeft();
  }

  cleanUp() {
    this.howl.unload();
    Howler.unload();
  }

  get howl(): Howl {
    if (this._howl == undefined) {
      this._howl = new Howl({
        src: [this.soundUrl],
        html5: !!navigator.userAgent.match(/(iPad)|(iPhone)|(iPod)|(android)/i),
        onload: this.howlOnLoad,
        onend: this.howlOnEnd,
      });
    }
    return this._howl;
  }

  //#region Utilities

  formatTime(secs: number) {
    const minutes = Math.floor(secs / 60) || 0;
    const seconds = Math.floor(secs - minutes * 60) || 0;
    return minutes + ":" + (seconds < 10 ? "0" : "") + seconds;
  }

  //#endregion

  //#region Timer

  updateTimeLeft() {
    if (this._howl == undefined) {
      return;
    }

    var timeLeft = this.declaredDuration;
    if (this.howl.state() === "loaded") {
      timeLeft = this.howl.duration() - this.safeSeek();
    }
    if (this.timeLeftLabel) {
      if (timeLeft != 0.0) {
        this.timeLeftLabel.innerHTML = this.formatTime(timeLeft);
        this.timeLeftLabel.style.opacity = "1.0";
      } else {
        this.timeLeftLabel.style.opacity = "0.0";
      }
    }
    if (this.playButton?.classList.contains("playing")) {
      if (!this.timerHandle) {
        this.timerHandle = window.setTimeout(this.updateTimeLeftTick, 1000);
      }
    } else {
      if (this.timerHandle) {
        window.clearTimeout(this.timerHandle);
        this.timerHandle = undefined;
      }
    }
  }

  updateTimeLeftTick = () => {
    this.timerHandle = undefined;
    this.updateTimeLeft();
  };

  //#endregion

  //#region Howler

  howlOnLoad = () => {
    if (this.playButton?.classList.contains("loading")) {
      this.playButton?.classList.remove("loading");
      this.howl.play();
    }
    this.updateTimeLeft();
  };

  howlOnEnd = () => {
    this.playButton?.classList.remove("playing");
    this.playButton?.classList.remove("loading");
    this.updateTimeLeft();
  };

  // Workaround a Howler bug where seek() returns an object
  safeSeek() {
    var time = Number(this.howl.seek());
    if (isNaN(time)) {
      time = (this.howl as any)._sounds[0]._seek;
    }
    return time;
  }

  //#endregion

  play() {
    if (this.playButton?.classList.contains("playing")) {
      this.playButton?.classList.remove("playing");
      this.playButton?.classList.remove("loading");
      this.howl.pause();
      this.updateTimeLeft();
    } else {
      this.playButton?.classList.add("playing");
      if (this.howl.state() === "loading") {
        this.playButton?.classList.add("loading");
      } else {
        this.howl.play();
      }
      this.updateTimeLeft();
    }
  }
}
